import {
  bloodTypeOptions,
  feedingTubeOptions,
  ivFluidOptions,
  ivMedicationOptions,
  ivMedicationUnitOptions,
  nebulisationDoseOptions,
  parentalFluidOptions,
  parentalMedicationOptions,
  parentalMedicationUnitOptions,
  pressureSoreAreaOptions,
  pressureSoreCareOptions,
  pressureSoreDressingOptions,
  pressureSoreGradeOptions,
  pressureSoreThromboprophylaxisOptions,
  respiratorySupportOptions,
  tracheostomyOptions,
  urinaryCatheterTypeOptions,
  urinaryFoleysCatheterOptions,
  woundCareAreaOptions,
  woundCareDressingOptions,
} from "../../Options/careItemsOptions";
import { yesNoOptions } from "../../../../constants/defaultKeys";

/*
 * What the nurse can raise as an SOS, grouped the way the care item tabs
 * group them: the group is picked first, then the type within it.
 *
 * The backend only schedules a care item once the order carries the fields its
 * own form marks required, so these mirror the doctor's order sheet forms
 * field for field - same names, same options, same order - and are submitted
 * to the same endpoint.
 *
 * Specialist Consultation is deliberately absent: it is the doctor's to
 * raise, so the nurse is not offered it here.
 *
 * Frequency is absent too. An SOS is given once, as needed - it is not put on
 * a schedule - so nothing here asks how often to repeat it.
 */
const SOS_CARE_GROUPS = [
  {
    key: "nebulization",
    label: "Nebulisation",
    items: [
    {
      careType: "nebulization",
      drugNameField: "name",
      label: "Nebulisation",
    fields: [
      {
        name: "name",
        label: "Name + Dose",
        type: "select",
        options: nebulisationDoseOptions,
        required: true,
      },
      { name: "notes", label: "Comments", type: "text" },
    ],
    },
    ],
  },
  {
    key: "iv_fluids",
    label: "IV Fluids",
    items: [
    {
      careType: "iv_fluids",
      drugNameField: "medication",
      label: "IV Fluids",
    fields: [
      {
        name: "fluids",
        label: "Fluids",
        type: "select",
        options: ivFluidOptions,
        required: true,
      },
      {
        name: "other_fluid",
        label: "Other Fluid",
        type: "text",
        required: true,
        showWhen: { field: "fluids", value: "others" },
      },
      {
        name: "rate",
        label: "Rate",
        type: "number",
        addonAfter: "ml/hr",
        required: true,
      },
      {
        name: "medication",
        label: "Medication",
        type: "select",
        options: ivMedicationOptions,
        required: true,
      },
      {
        name: "other_medication",
        label: "Other Medication",
        type: "text",
        required: true,
        showWhen: { field: "medication", value: "others" },
      },
      {
        name: "medication_unit",
        label: "Medication Unit",
        type: "select",
        options: ivMedicationUnitOptions,
        required: true,
      },
      {
        name: "medication_amount",
        label: "Medication Amount",
        type: "number",
        addonAfter: "ml/hr",
        required: true,
      },
      { name: "notes", label: "Comments", type: "text" },
    ],
    },
    ],
  },
  {
    key: "blood_products",
    label: "Blood Products",
    items: [
      {
        careType: "blood_products",
        drugNameField: "type",
        label: "Blood Products",
        fields: [
          {
            name: "type",
            label: "Type",
            type: "select",
            options: bloodTypeOptions,
            required: true,
          },
          {
            name: "other_type",
            label: "Other Type",
            type: "text",
            required: true,
            showWhen: { field: "type", value: "others" },
          },
          {
            name: "rate",
            label: "Rate",
            type: "number",
            addonAfter: "ml/hr",
            required: true,
          },
          { name: "notes", label: "Comments", type: "text" },
        ],
      },
    ],
  },
  {
    key: "parental_nutrition",
    label: "Parental Nutrition",
    items: [
      {
        careType: "parental_nutrition",
        drugNameField: "medication",
        label: "Parental Nutrition",
        fields: [
          {
            name: "fluids",
            label: "Fluids",
            type: "select",
            options: parentalFluidOptions,
            required: true,
          },
          {
            name: "other_fluids",
            label: "Other Fluids",
            type: "text",
            required: true,
            showWhen: { field: "fluids", value: "others" },
          },
          {
            name: "rate",
            label: "Rate",
            type: "number",
            addonAfter: "ml/hr",
            required: true,
          },
          {
            name: "medication",
            label: "Medication",
            type: "select",
            options: parentalMedicationOptions,
            required: true,
          },
          {
            name: "other_medication",
            label: "Other Medication",
            type: "text",
            required: true,
            showWhen: { field: "medication", value: "others" },
          },
          {
            name: "medication_amount",
            label: "Medication Amount",
            type: "number",
            addonAfter: "ml/hr",
            required: true,
          },
          {
            name: "medication_unit",
            label: "Medication Unit",
            type: "select",
            options: parentalMedicationUnitOptions,
            required: true,
          },
          { name: "notes", label: "Comments", type: "text" },
        ],
      },
    ],
  },
  {
    key: "feeding_tube",
    label: "Feeding Tube",
    items: [
    {
      careType: "feeding_tube",
      drugNameField: "feeding_tube_type",
      label: "Feeding Tube",
    fields: [
      {
        name: "feeding_tube_type",
        label: "Feeding Tube",
        type: "select",
        options: feedingTubeOptions,
        required: true,
      },
      { name: "notes", label: "Comments", type: "text" },
    ],
    },
    ],
  },
  {
    key: "urinary_catheter_care",
    label: "Urinary Catheter",
    items: [
    {
      careType: "urinary_catheter_care",
      drugNameField: "urinary_catheter_type",
      label: "Urinary Catheter",
    fields: [
      {
        name: "urinary_catheter_type",
        label: "Urinary Catheter Type",
        type: "select",
        options: urinaryCatheterTypeOptions,
        required: true,
      },
      {
        name: "foleys_catheter_type",
        label: "Foleys Catheter Type",
        type: "radio",
        options: urinaryFoleysCatheterOptions,
        showWhen: { field: "urinary_catheter_type", value: "foleys_catheter" },
      },
      /* Named as the doctor's form names it, so the comment reads back the
         same wherever the order is shown. */
      { name: "urinary_catheter_care_notes", label: "Comments", type: "text" },
    ],
    },
    ],
  },
  {
    key: "ostomy_care",
    label: "Ostomy Care",
    items: [
    {
      careType: "ostomy_care",
      drugNameField: "ostomy_care",
      label: "Ostomy Care",
    fields: [
      {
        name: "ostomy_care",
        label: "Ostomy Care",
        type: "select",
        options: yesNoOptions,
        required: true,
      },
      { name: "notes", label: "Comments", type: "text" },
    ],
    },
    ],
  },
  {
    key: "open_wound_care",
    label: "Wound Care",
    items: [
      {
        careType: "open_wound_care",
        drugNameField: "wound_area",
        label: "Wound Care",
        fields: [
          { name: "wound_area", label: "Wound Area", type: "select", options: woundCareAreaOptions, required: true },
          { name: "other_wound_area", label: "Other Wound Area", type: "text", required: true, showWhen: { field: "wound_area", value: "others" } },
          { name: "dressing", label: "Dressing", type: "select", options: woundCareDressingOptions, required: true },
          { name: "other_dressing_area", label: "Other Dressing Area", type: "text", required: true, showWhen: { field: "dressing", value: "others" } },
          { name: "vac_management", label: "VAC Management", type: "radio", options: yesNoOptions },
          { name: "debridement", label: "Debridement", type: "radio", options: yesNoOptions },
          { name: "suturing", label: "Suturing", type: "radio", options: yesNoOptions },
          { name: "notes", label: "Comments", type: "text" },
        ],
      },
    ],
  },
  {
    key: "pressure_sore",
    label: "Pressure Sore",
    items: [
      {
        careType: "pressure_sore",
        drugNameField: "pressure_sore_area",
        label: "Pressure Sore",
        fields: [
          { name: "pressure_sore_area", label: "Pressure Sore Area", type: "select", options: pressureSoreAreaOptions, required: true },
          { name: "other_sore", label: "Other Pressure Sore Area", type: "text", required: true, showWhen: { field: "pressure_sore_area", value: "others" } },
          { name: "pressure_sore_grade", label: "Grade", type: "select", options: pressureSoreGradeOptions, required: true },
          { name: "dressing", label: "Dressing", type: "select", options: pressureSoreDressingOptions, required: true },
          { name: "other_dressing_area", label: "Other Dressing Area", type: "text", required: true, showWhen: { field: "dressing", value: "others" } },
          { name: "vac_management", label: "VAC Management", type: "radio", options: yesNoOptions },
          { name: "debridement", label: "Debridement", type: "radio", options: yesNoOptions },
          { name: "notes", label: "Comments", type: "text" },
        ],
      },
    ],
  },
  {
    key: "preventive_care",
    label: "Preventive Care",
    items: [
    {
      careType: "position_change_schedule",
      label: "Position Change Schedule",
      /* No interval is asked for here. What the nurse adds is given once, as
         needed - the hourly schedule is the doctor's to set. */
    fields: [{ name: "notes", label: "Comments", type: "text" }],
    },
    {
      careType: "preventive_care_central_line_care",
      drugNameField: "central_line",
      label: "Central Line Care",
    fields: [
      {
        name: "central_line",
        label: "Central Line Care",
        type: "select",
        options: yesNoOptions,
        required: true,
      },
      { name: "notes", label: "Comments", type: "text" },
    ],
    },
    {
      careType: "preventive_care_pressure_sore_care",
      drugNameField: "pressure_sore_care",
      label: "Pressure Sore Care",
    fields: [
          { name: "pressure_sore_care", label: "Pressure Sore Care", type: "select", options: pressureSoreCareOptions, required: true },
          { name: "other_pressure_sore_care", label: "Other Pressure Sore Care", type: "text", required: true, showWhen: { field: "pressure_sore_care", value: "others" } },
          { name: "notes", label: "Comments", type: "text" },
    ],
    },
    {
      careType: "preventive_care_thromboprophylaxis",
      drugNameField: "thromboprophylaxis",
      label: "Thromboprophylaxis",
    fields: [
      {
        name: "thromboprophylaxis",
        label: "Thromboprophylaxis",
        type: "select",
        options: pressureSoreThromboprophylaxisOptions,
        required: true,
      },
      { name: "notes", label: "Comments", type: "text" },
    ],
    },
    ],
  },
  {
    key: "respiratory_care",
    label: "Respiratory Care",
    items: [
    {
      careType: "respiratory_care_chest_physiotherapy",
      drugNameField: "chest_physiotherapy",
      label: "Chest Physiotherapy",
    fields: [
  { name: "notes", label: "Comments", type: "text" },],
    },
    {
      careType: "respiratory_care_respiratory_support",
      drugNameField: "respiratory_support_type",
      label: "Respiratory Support",
      fields: [
          { name: "respiratory_support_type", label: "Respiratory Support", type: "select", options: respiratorySupportOptions.respiratorySupport, required: true },
          { name: "respiratory_support_type_oxygen_nasal_prongs", label: "Flow", type: "number", addonAfter: "Lts/Min", required: true, showWhen: { field: "respiratory_support_type", value: "oxygen_nasal_prongs" } },
          { name: "respiratory_support_type_thermo_ventilator", label: "Flow", type: "number", addonAfter: "Lts/Min", required: true, showWhen: { field: "respiratory_support_type", value: "thermo_ventilator" } },
          { name: "respiratory_support_type_oxygen_hudson_mask", label: "Flow", type: "number", addonAfter: "Lts/Min", required: true, showWhen: { field: "respiratory_support_type", value: "oxygen_hudson_mask" } },
          { name: "respiratory_support_type_oxygen_hi_flow_mask", label: "Flow", type: "number", addonAfter: "Lts/Min", required: true, showWhen: { field: "respiratory_support_type", value: "oxygen_hi_flow_mask" } },
          { name: "respiratory_support_type_oxygen_rebreathing_mask", label: "Flow", type: "number", addonAfter: "Lts/Min", required: true, showWhen: { field: "respiratory_support_type", value: "oxygen_rebreathing_mask" } },
          { name: "bipap_support_epap", label: "EPAP", type: "number", addonAfter: "Cm of H2O", required: true, showWhen: { field: "respiratory_support_type", value: "bipap_support" } },
          { name: "bipap_support_ipap", label: "IPAP", type: "number", addonAfter: "Cm of H2O", required: true, showWhen: { field: "respiratory_support_type", value: "bipap_support" } },
          { name: "bipap_support_rr", label: "RR", type: "number", addonAfter: "/min", required: true, showWhen: { field: "respiratory_support_type", value: "bipap_support" } },
          { name: "bipap_support_fio2", label: "FiO2", type: "number", addonAfter: "%", required: true, showWhen: { field: "respiratory_support_type", value: "bipap_support" } },
        { name: "notes", label: "Comments", type: "text" },
      ],
    },
    {
      careType: "respiratory_care_tracheostomy_care",
      drugNameField: "tracheostomy",
      label: "Tracheostomy Care",
    fields: [
          { name: "tracheostomy", label: "Tracheostomy", type: "select", options: tracheostomyOptions, required: true },
          { name: "tracheostomy_size", label: "Size", type: "number" },
          { name: "decannulation_trial", label: "Trial of Decannulation", type: "radio", options: yesNoOptions },
          { name: "notes", label: "Comments", type: "text" },
    ],
    },
    ],
  },
  {
    key: "eye_care",
    label: "Eye Care",
    items: [
    {
      careType: "eye_care_eye_drops",
      drugNameField: "eye_drops",
      label: "Eye Drops",
    fields: [
  { name: "notes", label: "Comments", type: "text" },],
    },
    {
      careType: "eye_care_eye_gel_ointment",
      drugNameField: "eye_gel",
      label: "Eye Gel / Ointment",
    fields: [
  { name: "notes", label: "Comments", type: "text" },],
    },
    {
      careType: "eye_care_saline_wash",
      drugNameField: "saline_wash",
      label: "Saline Wash",
    fields: [
  { name: "notes", label: "Comments", type: "text" },],
    },
      {
        careType: "eye_care_eye_patching_guard",
        label: "Eye Guard",
        flag: "eye_guard",
        fields: [
  { name: "notes", label: "Comments", type: "text" },],
      },
      {
        careType: "eye_care_trasorrhaphy",
        label: "Trasorrhaphy",
        flag: "tarsorrhaphy",
        fields: [
  { name: "notes", label: "Comments", type: "text" },],
      },
    ],
  },
];

export default SOS_CARE_GROUPS;

/* A care type counts as already ordered when any key of the day's care items
   starts with it - the same test the tabs use, so this offers exactly what the
   tabs do not already show. Groups with nothing left to offer drop out. */
export const getUnorderedGroups = (formData) => {
  const orderedKeys = Object.keys(formData || {});
  return SOS_CARE_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(
      ({ careType }) => !orderedKeys.some((key) => key.startsWith(careType))
    ),
  })).filter((group) => group.items.length);
};

/*
 * The enable endpoint takes the item's headline choice as `drug_name` and the
 * rest as `data` - the nurse care item components read `drug_name` back to
 * title each card. `drugNameField` names which field that is.
 */
export const splitEnablePayload = (item, careValues = {}) => {
  const visible = (item.fields || []).filter((field) =>
    isFieldVisible(field, careValues)
  );
  const data = {};
  let drugName = "";
  /* Ostomy Care and Central Line Care are named by a yes or no answer, not by
     a choice of type. "No" means that care is not being given, so there is
     nothing to add; "Yes" means the care item's own name is what names it -
     stored as the answer, the table titled itself "yes". */
  const namedByYesNo =
    (item.fields || []).find((field) => field.name === item.drugNameField)
      ?.options === yesNoOptions;
  visible.forEach((field) => {
    const value = careValues[field.name];
    if (value === undefined || value === null || value === "") return;
    if (field.name === item.drugNameField) drugName = value;
    /* The field that names the care item is sent twice - as `drug_name`,
       which is what the cards title themselves with, and inside `data`,
       where the doctor's own order keeps it. */
    data[field.name] = value;
  });
  /* Nothing to choose (eye guard, trasorrhaphy) - the flag is the item, and
     it is sent the way the doctor's form sends it. */
  if (item.flag) {
    data[item.flag] = "yes";
    return { drugName: item.label, data };
  }
  if (namedByYesNo) {
    if (drugName === "no") return { declined: true, drugName: "", data };
    return { drugName: item.label, data };
  }
  if (!visible.length) return { drugName: item.label, data: {} };
  return { drugName, data };
};

/* A field only applies when its `showWhen` parent holds the matching value -
   the "Others" inputs behave the same way on the doctor's form. */
export const isFieldVisible = (field, careValues) =>
  !field.showWhen || careValues?.[field.showWhen.field] === field.showWhen.value;
